import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

/**
 * ForgotPassword — first step of the password-reset flow. Always shows a
 * generic success message on submit to prevent email enumeration.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Server should always return 200 — treat 4xx as a soft error
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1 text-sm text-[#4A6B56] hover:text-[#1B3624] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <Card className="p-7 sm:p-8 rounded-3xl border-[#E2DFD8] bg-white">
          {!sent ? (
            <>
              <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide uppercase">
                <Mail className="w-3 h-3" /> Password recovery
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl tracking-tight font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }} data-testid="forgot-title">
                Forgot your password?
              </h1>
              <p className="mt-2 text-sm text-[#6B6965]">
                Enter the email you signed up with and we'll send you a link to reset it. Expires in 30 minutes.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email" className="text-sm">Email</Label>
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.co.uk"
                    className="rounded-xl"
                  />
                </div>
                <Button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white py-6"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success" className="text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-[#4A6B56]/15 text-[#4A6B56] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="mt-4 text-xl sm:text-2xl tracking-tight font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-[#6B6965]">
                If <span className="text-[#1A1A1A] font-medium">{email}</span> is a registered account, we've sent a reset link. It expires in 30 minutes.
              </p>
              <p className="mt-4 text-xs text-[#9B9893]">
                Not seeing it? Check spam, or{" "}
                <button
                  type="button"
                  data-testid="forgot-retry"
                  className="text-[#4A6B56] underline hover:text-[#1B3624]"
                  onClick={() => setSent(false)}
                >
                  try a different email
                </button>.
              </p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
